import { AlertCircle, CheckCircle2, Clock, Loader2, Mail, Phone, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Instagram } from "../../components/common/BrandIcons";
import { SectionTitle } from "../../components/common/SectionTitle";
import { getContactDetails } from "../../constants/contact";
import { EMAILJS_IS_CONFIGURED } from "../../constants/site";
import { sendContactMessage } from "../../utils/emailClient";

// ---------------------------------------------------------------------------
// Contact info cards — bug-fix note:
//
// The previous version of this file had:
//   value: "4521 Westheimer Road\Canton, TX 77027"
// `\C` is not a recognised JavaScript escape, so the engine silently drops
// the backslash and the string renders as one run-together line. The line
// below is the corrected version with a real "\n" newline.
// ---------------------------------------------------------------------------
const CONTACT_ITEMS = [
  {
    label: "Phone",
    revealKey: "phone",
    reveal: "Show phone number",
    icon: Phone,
  },
  {
    label: "Email",
    revealKey: "email",
    reveal: "Show email",
    icon: Mail,
  },
  {
    label: "Hours",
    value: "Mon–Thu: 8AM – 10PM\nSat–Sun: Closed",
    icon: Clock,
  },
  {
    label: "Instagram",
    value: "@styledby.Trinya.Marieee",
    href: "https://www.instagram.com/styledby.Trinya.Marieee/",
    icon: Instagram,
  },
];

// Phone and email stay out of the page until a visitor taps "Show" (see
// constants/contact.js), so spam bots scanning the page don't find them.
function resolveContactItem(item, contact) {
  if (!item.revealKey || !contact) return item;
  return item.revealKey === "phone"
    ? { ...item, value: contact.phoneDisplay, href: contact.phoneTel, reveal: null }
    : { ...item, value: contact.email, href: `mailto:${contact.email}`, reveal: null };
}

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
  // Honeypot: hidden field that real visitors never see or fill. Bots that
  // fill every input trip it, and utils/emailClient.js quietly drops the
  // message while showing the bot a normal "sent" message.
  honeypot: "",
};

export function ContactSection() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState({ kind: "idle", message: "" });
  // Spam trap: when the form appeared. A message sent within seconds of that
  // can only come from a bot (see utils/emailClient.js).
  const startedAtRef = useRef(Date.now());
  // Which contact details the visitor has tapped "Show" on.
  const [revealed, setRevealed] = useState({});
  const pendingFocus = useRef(null);
  const details = revealed.phone || revealed.email ? getContactDetails() : null;

  // Keyboard users: move focus from the (now removed) Show button to the link.
  useEffect(() => {
    if (!pendingFocus.current) return;
    document.querySelector(`#contact [data-reveal-key="${pendingFocus.current}"]`)?.focus();
    pendingFocus.current = null;
  }, [revealed]);

  function reveal(key) {
    pendingFocus.current = key;
    setRevealed((current) => ({ ...current, [key]: true }));
  }

  const isSubmitting = status.kind === "submitting";
  const isSuccess = status.kind === "success";

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (isSubmitting) return;

    setStatus({ kind: "submitting", message: "" });

    try {
      await sendContactMessage({ ...form, startedAt: startedAtRef.current });
      setStatus({
        kind: "success",
        message: "Thanks — your message is on its way. We'll reply soon.",
      });
      setForm(INITIAL_FORM);
      startedAtRef.current = Date.now();
    } catch (err) {
      setStatus({
        kind: "error",
        message: err?.message || "We couldn't send your message. Please try again.",
      });
    }
  }

  return (
    <section id="contact" className="site-section">
      <div className="container">
        <SectionTitle subtitle="Visit Us" title="Get in Touch" />

        <div className="contact-grid scroll-reveal">
          {CONTACT_ITEMS.map((entry) => {
            const item = resolveContactItem(entry, revealed[entry.revealKey] ? details : null);
            const Icon = item.icon;
            return (
              <article key={item.label} className="contact-card">
                <div className="contact-card__icon">
                  <Icon size={24} />
                </div>
                <p className="eyebrow">{item.label}</p>
                {item.reveal ? (
                  <button type="button" className="contact-card__reveal" onClick={() => reveal(item.revealKey)}>
                    {item.reveal}
                  </button>
                ) : item.href ? (
                  <a
                    className="contact-card__link"
                    data-reveal-key={item.revealKey}
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    style={{ whiteSpace: "pre-line" }}
                  >
                    {item.value}
                  </a>
                ) : (
                  <p className="contact-card__text" style={{ whiteSpace: "pre-line" }}>
                    {item.value}
                  </p>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
